import Phaser from 'phaser';
import { CardRenderer } from '../objects/CardRenderer';
import { PERSIAN_THEME } from '../theme/persianTheme';
import { applyRenderScale, RENDER_SCALE } from '../renderScale';
import { markStartupComplete } from '../utils/iOSCrashRecovery';
import { MysticalTitle } from '../objects/MysticalTitle';

export class PreloadScene extends Phaser.Scene {
  private mysticalTitle?: MysticalTitle;

  constructor() {
    super({ key: 'PreloadScene' });
  }

  preload(): void {
    // Create centered title without particles (textures don't exist yet)
    this.mysticalTitle = new MysticalTitle(this, { centered: true, skipParticles: true });
  }

  create(): void {
    applyRenderScale(this);

    // Use camera background color to cover the screen during loading
    this.cameras.main.setBackgroundColor(PERSIAN_THEME.colors.feltGreen);

    // Generate felt texture for background
    this.generateFeltTexture();

    // Generate gold dust particle texture for title particles
    this.generateGoldDustTexture();

    // Generate all card textures procedurally
    const cardRenderer = new CardRenderer(this);
    cardRenderer.generateAllCardTextures();

    // Textures generated successfully - mark startup complete for iOS crash recovery
    markStartupComplete();

    // Animate title up to its normal position, then transition to lobby
    this.mysticalTitle?.animateToPosition(55, 600).then(() => {
      // Transition to LobbyScene (replaces this scene)
      this.scene.start('LobbyScene');
    });
  }

  update(time: number, _delta: number): void {
    // Update title flicker animation
    this.mysticalTitle?.update(time);
  }

  private generateFeltTexture(): void {
    const S = RENDER_SCALE;
    const width = 512 * S;
    const height = 512 * S;
    const graphics = this.make.graphics({});

    // Base color - Persian deep teal-green
    graphics.fillStyle(PERSIAN_THEME.colors.feltGreen);
    graphics.fillRect(0, 0, width, height);

    // Add subtle noise pattern for felt effect (scale particle count with area)
    const particleCount = Math.floor(2000 * S * S);
    for (let i = 0; i < particleCount; i++) {
      const x = Math.random() * width;
      const y = Math.random() * height;
      const alpha = Math.random() * 0.08;
      const shade = Math.random() > 0.5 ? 0x2a5a4a : 0x0a3a2a;
      graphics.fillStyle(shade, alpha);
      graphics.fillCircle(x, y, (Math.random() * 2 + 0.5) * S);
    }

    // Add subtle Persian-inspired geometric pattern
    graphics.lineStyle(1 * S, 0x2a5a4a, 0.06);
    const spacing = 40 * S;
    for (let x = spacing / 2; x < width; x += spacing) {
      for (let y = spacing / 2; y < height; y += spacing) {
        // Small diamond pattern
        const size = 6 * S;
        graphics.beginPath();
        graphics.moveTo(x, y - size);
        graphics.lineTo(x + size, y);
        graphics.lineTo(x, y + size);
        graphics.lineTo(x - size, y);
        graphics.closePath();
        graphics.strokePath();
      }
    }

    graphics.generateTexture('felt-texture', width, height);
    graphics.destroy();
  }

  private generateGoldDustTexture(): void {
    const key = 'gold-dust-particle';
    if (this.textures.exists(key)) return;

    const size = Math.ceil(8 * RENDER_SCALE);
    const graphics = this.make.graphics({});
    graphics.fillStyle(0xffffff);
    graphics.fillCircle(size / 2, size / 2, size / 2);
    graphics.generateTexture(key, size, size);
    graphics.destroy();
  }
}
